// Mapa choropleth de San Juan a nivel departamento — SVG plano sin libs.
// - Proyección equirectangular (suficiente para una provincia chica)
// - Escala de color por RANGO percentil (no lineal): cada depto se ordena
//   y se le asigna color por su posición. Así siempre se ven los matices,
//   aunque el máximo sea muy grande respecto al promedio.
// - Zoom (rueda) + pan (drag) sobre el SVG, manipulando el viewBox.

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Box, Tooltip, Typography, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { SAN_JUAN_GEOJSON } from './sanJuanGeo';

const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');
const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

// 7 niveles de calor — del verde claro al rojo intenso
const COLORS = ['#e8f5e9', '#a5d6a7', '#fff59d', '#ffcc80', '#ff8a65', '#ef5350', '#b71c1c'];
const COLOR_SIN_DATOS = '#eceff1';
const COLOR_BORDE = '#37474f';

function calcularBBox(geojson) {
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    const procesar = (coords) => {
        if (typeof coords[0] === 'number') {
            const [lng, lat] = coords;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        } else { coords.forEach(procesar); }
    };
    geojson.features.forEach(f => procesar(f.geometry.coordinates));
    return { minLng, maxLng, minLat, maxLat };
}

function anilloAPath(anillo, proy) {
    if (!anillo.length) return '';
    return anillo.map(([lng, lat], i) => {
        const [x, y] = proy(lng, lat);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ') + 'Z';
}

function geometryAPath(geometry, proy) {
    if (geometry.type === 'Polygon') return geometry.coordinates.map(a => anilloAPath(a, proy)).join(' ');
    if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat().map(a => anilloAPath(a, proy)).join(' ');
    return '';
}

function centroide(geometry, proy) {
    let coords;
    if (geometry.type === 'Polygon') coords = geometry.coordinates[0];
    else if (geometry.type === 'MultiPolygon') {
        coords = geometry.coordinates.reduce((a, b) => a[0].length >= b[0].length ? a : b)[0];
    } else return [0, 0];
    let sx = 0, sy = 0;
    coords.forEach(([lng, lat]) => { const [x, y] = proy(lng, lat); sx += x; sy += y; });
    return [sx / coords.length, sy / coords.length];
}

const VIEW_W = 600;
const VIEW_H = 700;
const PADDING = 20;

export default function MapaCalorSanJuan({ valores, onClickDepto }) {
    const [hover, setHover] = useState(null);
    const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: VIEW_W, h: VIEW_H });
    const dragRef = useRef(null);
    const svgRef = useRef(null);

    const valoresMap = useMemo(() => {
        const m = {};
        (valores || []).forEach(v => { m[v.depto] = v; });
        return m;
    }, [valores]);

    // Color por RANGO percentil: ordeno los deptos por cantidad y a cada uno
    // le asigno un color según su posición (0..n-1) dividido en 7 buckets.
    // Así se distinguen todos, sin importar la escala absoluta.
    const colorPorDepto = useMemo(() => {
        const ordenados = (valores || [])
            .map(v => ({ depto: v.depto, cant: Number(v.cantidad || 0) }))
            .filter(v => v.cant > 0)
            .sort((a, b) => a.cant - b.cant);
        const map = {};
        const n = ordenados.length;
        ordenados.forEach((v, i) => {
            const idx = Math.min(COLORS.length - 1, Math.floor((i / Math.max(1, n - 1)) * (COLORS.length - 1) + 0.5));
            // Si todos tienen el mismo valor, todos van al medio
            map[v.depto] = COLORS[idx];
        });
        return map;
    }, [valores]);

    // Proyección + paths (cacheado, no depende de zoom/pan)
    const { paths } = useMemo(() => {
        const bbox = calcularBBox(SAN_JUAN_GEOJSON);
        const dLng = bbox.maxLng - bbox.minLng;
        const dLat = bbox.maxLat - bbox.minLat;
        const sx = (VIEW_W - 2 * PADDING) / dLng;
        const sy = (VIEW_H - 2 * PADDING) / dLat;
        const scale = Math.min(sx, sy);
        const offsetX = (VIEW_W - dLng * scale) / 2;
        const offsetY = (VIEW_H - dLat * scale) / 2;
        const proy = (lng, lat) => [
            (lng - bbox.minLng) * scale + offsetX,
            (bbox.maxLat - lat) * scale + offsetY,
        ];
        // Calculamos también un "tamaño" aproximado del polígono (área del bounding box)
        // para decidir si el label cabe dentro o lo escondemos en favor de mostrar los grandes
        const calcArea = (geometry) => {
            let coords;
            if (geometry.type === 'Polygon') coords = geometry.coordinates[0];
            else coords = geometry.coordinates.reduce((a, b) => a[0].length >= b[0].length ? a : b)[0];
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            coords.forEach(([lng, lat]) => {
                const [x, y] = proy(lng, lat);
                if (x < minX) minX = x; if (x > maxX) maxX = x;
                if (y < minY) minY = y; if (y > maxY) maxY = y;
            });
            return (maxX - minX) * (maxY - minY);
        };
        const paths = SAN_JUAN_GEOJSON.features.map(f => ({
            depto: f.properties.departamento,
            d: geometryAPath(f.geometry, proy),
            centroide: centroide(f.geometry, proy),
            area: calcArea(f.geometry),
        }));
        return { paths };
    }, []);

    // Si está zoomeado más de cierto factor, mostramos labels también para
    // los deptos chiquitos (centro de la provincia). Y el tamaño de fuente y
    // grosor de borde escalan inversamente al zoom para mantener tamaño visual.
    const escalaActual = VIEW_W / viewBox.w;
    const umbralAreaLabel = 800 / Math.max(1, escalaActual * escalaActual);
    const fontDepto = 9 / Math.max(1, escalaActual);
    const fontCant = 10 / Math.max(1, escalaActual);
    const strokeBase = 0.6 / Math.max(1, escalaActual);
    const strokeHover = 1.8 / Math.max(1, escalaActual);
    const labelStrokeW = 2 / Math.max(1, escalaActual);

    // Handlers de zoom y pan
    const zoom = (factor, cx, cy) => {
        setViewBox(vb => {
            const nW = Math.max(VIEW_W * 0.05, Math.min(VIEW_W * 4, vb.w / factor));
            const nH = Math.max(VIEW_H * 0.05, Math.min(VIEW_H * 4, vb.h / factor));
            const ratioX = cx != null ? (cx - vb.x) / vb.w : 0.5;
            const ratioY = cy != null ? (cy - vb.y) / vb.h : 0.5;
            const nX = (cx != null ? cx : vb.x + vb.w / 2) - ratioX * nW;
            const nY = (cy != null ? cy : vb.y + vb.h / 2) - ratioY * nH;
            return { x: nX, y: nY, w: nW, h: nH };
        });
    };

    // Wheel listener manual con passive: false — React 17+ usa pasivo por
    // default y preventDefault() es silenciosamente ignorado, dejando que
    // la rueda haga scroll de la página entera.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const handler = (e) => {
            e.preventDefault();
            const rect = svg.getBoundingClientRect();
            const cx = viewBox.x + ((e.clientX - rect.left) / rect.width) * viewBox.w;
            const cy = viewBox.y + ((e.clientY - rect.top) / rect.height) * viewBox.h;
            zoom(e.deltaY < 0 ? 1.2 : 1 / 1.2, cx, cy);
        };
        svg.addEventListener('wheel', handler, { passive: false });
        return () => svg.removeEventListener('wheel', handler);
    }, [viewBox]);

    const onMouseDown = (e) => {
        // Solo iniciamos drag si no se hizo click sobre un path (lo deja
        // pasar el evento). Marcamos timestamp para distinguir click vs drag.
        dragRef.current = { sx: e.clientX, sy: e.clientY, vbx: viewBox.x, vby: viewBox.y, moved: 0 };
    };

    const onMouseMove = (e) => {
        if (!dragRef.current) return;
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const dx = ((e.clientX - dragRef.current.sx) / rect.width) * viewBox.w;
        const dy = ((e.clientY - dragRef.current.sy) / rect.height) * viewBox.h;
        dragRef.current.moved = Math.abs(e.clientX - dragRef.current.sx) + Math.abs(e.clientY - dragRef.current.sy);
        if (dragRef.current.moved < 4) return;
        setViewBox(vb => ({ ...vb, x: dragRef.current.vbx - dx, y: dragRef.current.vby - dy }));
    };

    const onMouseUp = () => { dragRef.current = null; };

    const reset = () => setViewBox({ x: 0, y: 0, w: VIEW_W, h: VIEW_H });

    return (
        <Box sx={{ position: 'relative', width: '100%', maxWidth: VIEW_W, mx: 'auto' }}>
            {/* Controles de zoom */}
            <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 6, right: 6, zIndex: 1, bgcolor: 'rgba(255,255,255,0.95)', borderRadius: '4px', boxShadow: 1 }}>
                <IconButton size="small" onClick={() => zoom(1.3)} title="Acercar"><AddIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => zoom(1 / 1.3)} title="Alejar"><RemoveIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={reset} title="Restablecer"><RestartAltIcon fontSize="small" /></IconButton>
            </Stack>
            <svg
                ref={svgRef}
                viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                style={{ width: '100%', height: 'auto', cursor: dragRef.current ? 'grabbing' : 'grab', userSelect: 'none', touchAction: 'none' }}
            >
                {paths.map(p => {
                    const v = valoresMap[p.depto];
                    const fill = v ? (colorPorDepto[p.depto] || COLORS[0]) : COLOR_SIN_DATOS;
                    const isHover = hover === p.depto;
                    return (
                        <Tooltip
                            key={p.depto}
                            arrow
                            title={
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{p.depto}</Typography>
                                    {v ? (
                                        <>
                                            <Typography sx={{ fontSize: '0.75rem' }}>Expedientes: <strong>{fmtNum(v.cantidad)}</strong></Typography>
                                            <Typography sx={{ fontSize: '0.75rem' }}>Monto: <strong>{fmtMoney(v.monto)}</strong></Typography>
                                        </>
                                    ) : <Typography sx={{ fontSize: '0.75rem' }}>Sin datos</Typography>}
                                    <Typography sx={{ fontSize: '0.7rem', mt: 0.5, opacity: 0.7 }}>(Click para ver detalle)</Typography>
                                </Box>
                            }
                            placement="top"
                        >
                            <path
                                d={p.d}
                                fill={fill}
                                stroke={COLOR_BORDE}
                                strokeWidth={isHover ? strokeHover : strokeBase}
                                onMouseEnter={() => setHover(p.depto)}
                                onMouseLeave={() => setHover(null)}
                                onClick={() => {
                                    // Solo dispara click si no fue drag
                                    if (dragRef.current && dragRef.current.moved >= 4) return;
                                    if (onClickDepto) onClickDepto(p.depto, v);
                                }}
                                style={{ cursor: 'pointer', transition: 'stroke-width 0.15s' }}
                            />
                        </Tooltip>
                    );
                })}
                {/* Etiquetas — solo los deptos suficientemente grandes para el zoom actual.
                    El tamaño de fuente y stroke escalan inversamente al zoom para mantener
                    tamaño visual constante (la propiedad es en unidades del SVG, que se
                    multiplican por el zoom efectivo del viewBox). */}
                {paths.map(p => {
                    if (p.area < umbralAreaLabel) return null;
                    const v = valoresMap[p.depto];
                    const cant = v?.cantidad || 0;
                    return (
                        <g key={`label-${p.depto}`} pointerEvents="none">
                            <text x={p.centroide[0]} y={p.centroide[1] - 1}
                                textAnchor="middle"
                                fontSize={fontDepto}
                                fontWeight="700"
                                fill="#263238"
                                style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: labelStrokeW }}>
                                {p.depto}
                            </text>
                            {cant > 0 && (
                                <text x={p.centroide[0]} y={p.centroide[1] + fontCant}
                                    textAnchor="middle"
                                    fontSize={fontCant}
                                    fontWeight="700"
                                    fill="#b71c1c"
                                    style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: labelStrokeW }}>
                                    {fmtNum(cant)}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
            {/* Leyenda + ayuda */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>Menos</Typography>
                    {COLORS.map((c, i) => (
                        <Box key={i} sx={{ width: 22, height: 12, bgcolor: c, border: '1px solid #ddd' }} />
                    ))}
                    <Typography variant="caption" sx={{ color: '#666' }}>Más</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                    Rueda del mouse para zoom · arrastra para mover
                </Typography>
            </Box>
        </Box>
    );
}
